import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const pad2 = (n) => String(n).padStart(2, "0");

const formatMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${pad2(mins)}:${pad2(secs)}`;
};

const storageKeys = (userSessionId) => ({
  endAt: `sessionTimer:endAt:${userSessionId}`,
  pausedRemaining: `sessionTimer:pausedRemaining:${userSessionId}`,
  autoPaused: `sessionTimer:autoPaused:${userSessionId}`,
});

/**
 * A countdown timer that stays correct across tab backgrounding / mobile lock.
 *
 * Source of truth:
 * - running: sessionStorage(endAtMs)
 * - paused:  sessionStorage(pausedRemainingSeconds)
 *
 * Backend sync:
 * - call `onDurationChange(spentSeconds)` throttled to once per `syncEveryMs`
 * - also sync when tab becomes visible
 */
export default function useSessionTimer({
  show,
  userSessionId,
  totalSeconds,
  initialSpentSeconds = 0,
  isPaused = false,
  syncEveryMs = 10000,
  onExpire,
  onDurationChange,
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  // Track the last duration we SENT to backend (so we don't spam duplicates)
  const lastSentSpentRef = useRef(null);
  const intervalRef = useRef(null);
  const prevShowRef = useRef(show);

  const keys = useMemo(
    () => (userSessionId ? storageKeys(userSessionId) : null),
    [userSessionId]
  );

  const getNowRemaining = useCallback(() => {
    if (!keys) return 0;

    const pausedRemainingRaw = sessionStorage.getItem(keys.pausedRemaining);
    if (pausedRemainingRaw != null) {
      const pausedRemaining = Number(pausedRemainingRaw);
      return Number.isFinite(pausedRemaining) ? pausedRemaining : 0;
    }

    const endAtRaw = sessionStorage.getItem(keys.endAt);
    if (!endAtRaw) return 0;
    const endAtMs = Number(endAtRaw);
    if (!Number.isFinite(endAtMs)) return 0;
    return Math.max(0, Math.ceil((endAtMs - Date.now()) / 1000));
  }, [keys]);

  const ensureInitialized = useCallback(() => {
    if (!keys) return;

    // If pausedRemaining exists, we're initialized (paused mode).
    const pausedRemainingRaw = sessionStorage.getItem(keys.pausedRemaining);
    if (pausedRemainingRaw != null) return;

    // If endAt exists, we're initialized (running mode).
    const existingEndAt = sessionStorage.getItem(keys.endAt);
    if (existingEndAt) return;

    // Otherwise derive state from backend-known spent seconds.
    const spent = Math.max(0, Math.min(totalSeconds, initialSpentSeconds || 0));
    const remaining = Math.max(0, totalSeconds - spent);

    // If UI is currently paused, initialize in paused mode to avoid races where
    // the user pauses before we ever persist endAt.
    if (isPaused) {
      sessionStorage.setItem(keys.pausedRemaining, String(remaining));
      sessionStorage.removeItem(keys.endAt);
      setRemainingSeconds(remaining);
      return;
    }

    const endAtMs = Date.now() + remaining * 1000;
    sessionStorage.setItem(keys.endAt, String(endAtMs));
  }, [keys, totalSeconds, initialSpentSeconds, isPaused]);

  const setPaused = useCallback(
    (paused) => {
      if (!keys) return;

      if (paused) {
        // Already paused -> no-op
        if (sessionStorage.getItem(keys.pausedRemaining) != null) return;

        // Convert running endAt -> pausedRemaining
        // Prefer the currently-rendered remaining seconds to avoid timing races.
        const remaining =
          Number.isFinite(remainingSeconds) && remainingSeconds > 0
            ? remainingSeconds
            : getNowRemaining();
        sessionStorage.setItem(keys.pausedRemaining, String(remaining));
        sessionStorage.removeItem(keys.endAt);
        setRemainingSeconds(remaining);
      } else {
        // Already running -> no-op
        if (sessionStorage.getItem(keys.endAt)) return;

        // Convert pausedRemaining -> running endAt
        const pausedRemainingRaw = sessionStorage.getItem(keys.pausedRemaining);
        // If we weren't paused (nothing stored), don't reset anything.
        if (pausedRemainingRaw == null) return;
        const pausedRemaining = Number(pausedRemainingRaw);
        const remaining = Number.isFinite(pausedRemaining)
          ? Math.max(0, pausedRemaining)
          : 0;
        sessionStorage.removeItem(keys.pausedRemaining);
        const endAtMs = Date.now() + remaining * 1000;
        sessionStorage.setItem(keys.endAt, String(endAtMs));
        setRemainingSeconds(remaining);
      }
    },
    [keys, getNowRemaining, remainingSeconds]
  );

  // Freeze timer when popup is hidden so reopening doesn't "lose" seconds.
  // This is UI-only behavior: the session clock is effectively paused while the modal is closed.
  useEffect(() => {
    if (!keys || !userSessionId) return;

    const prevShow = prevShowRef.current;
    prevShowRef.current = show;

    // Transition: visible -> hidden
    if (prevShow && !show) {
      // Only auto-pause if the session isn't already paused.
      if (!isPaused) {
        // If running, store a frozen remaining value.
        if (sessionStorage.getItem(keys.endAt)) {
          const remaining =
            Number.isFinite(remainingSeconds) && remainingSeconds >= 0
              ? remainingSeconds
              : getNowRemaining();
          sessionStorage.setItem(keys.pausedRemaining, String(remaining));
          sessionStorage.removeItem(keys.endAt);
          sessionStorage.setItem(keys.autoPaused, "1");
        }
      }
      return;
    }

    // Transition: hidden -> visible
    if (!prevShow && show) {
      const wasAutoPaused = sessionStorage.getItem(keys.autoPaused) === "1";
      if (wasAutoPaused && !isPaused) {
        // Resume from the frozen paused value.
        const pausedRemainingRaw = sessionStorage.getItem(keys.pausedRemaining);
        const pausedRemaining = Number(pausedRemainingRaw);
        const remaining = Number.isFinite(pausedRemaining)
          ? Math.max(0, pausedRemaining)
          : 0;
        sessionStorage.removeItem(keys.pausedRemaining);
        sessionStorage.removeItem(keys.autoPaused);
        const endAtMs = Date.now() + remaining * 1000;
        sessionStorage.setItem(keys.endAt, String(endAtMs));
        setRemainingSeconds(remaining);
      } else if (!wasAutoPaused) {
        // Clean up any stale autoPaused marker.
        sessionStorage.removeItem(keys.autoPaused);
      }
    }
  }, [show, keys, userSessionId, isPaused, remainingSeconds, getNowRemaining]);

  const reset = useCallback(() => {
    if (!keys) return;
    sessionStorage.removeItem(keys.endAt);
    sessionStorage.removeItem(keys.pausedRemaining);
    setRemainingSeconds(0);
    lastSentSpentRef.current = null;
  }, [keys]);

  const tick = useCallback(() => {
    const remaining = getNowRemaining();
    setRemainingSeconds(remaining);

    if (remaining <= 0 && typeof onExpire === "function") {
      onExpire();
    }
  }, [getNowRemaining, onExpire, onDurationChange, totalSeconds]);

  // Initialize + keep pause state in sync with storage.
  useEffect(() => {
    if (!show || !userSessionId || !Number.isFinite(totalSeconds)) return;
    ensureInitialized();
  }, [show, userSessionId, totalSeconds, initialSpentSeconds, ensureInitialized]);

  // Apply pause/resume changes (idempotent).
  useEffect(() => {
    if (!show || !userSessionId) return;
    setPaused(!!isPaused);
  }, [show, userSessionId, isPaused, setPaused]);

  // Start ticking (UI refresh). This is safe even when backgrounded because we
  // derive from Date.now() each time.
  useEffect(() => {
    if (!show || !userSessionId) return;

    tick(); // immediate
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [show, userSessionId, tick]);

  // Throttled backend sync for duration.
  useEffect(() => {
    if (!show || !userSessionId) return;
    if (typeof onDurationChange !== "function") return;

    const sync = () => {
      const remaining = getNowRemaining();
      const spent = Math.max(0, totalSeconds - remaining);
      // Don't spam backend with identical values.
      if (lastSentSpentRef.current !== spent) {
        lastSentSpentRef.current = spent;
        onDurationChange(spent);
      }
    };

    // Sync periodically.
    const syncInterval = setInterval(sync, syncEveryMs);

    // Sync on tab becoming visible (mobile unlock / tab switch).
    const onVis = () => {
      if (document.visibilityState === "visible") {
        tick();
        sync();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    // Sync when page is being hidden/unloaded (covers refresh/back/close on mobile better than beforeunload alone)
    const onPageHide = () => {
      try {
        sync();
      } catch (e) {
        // swallow
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      clearInterval(syncInterval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [
    show,
    userSessionId,
    onDurationChange,
    syncEveryMs,
    getNowRemaining,
    totalSeconds,
    tick,
  ]);

  const spentSeconds = useMemo(
    () => Math.max(0, totalSeconds - remainingSeconds),
    [totalSeconds, remainingSeconds]
  );

  const timerText = useMemo(
    () => formatMMSS(remainingSeconds),
    [remainingSeconds]
  );

  return {
    timerText,
    remainingSeconds,
    spentSeconds,
    setPaused,
    reset,
  };
}


